// ============================================
// ゲーム設定（CONFIG）
// ゲームのバランス調整はここで行う
// ============================================

interface DifficultySettings {
    name: string;
    color: readonly [number, number, number];
    enemySpeedMult: number;
    enemyHpMult: number;
    spawnIntervalMult: number;
    itemDropMult: number;
    bossHpMult: number;
    startBombs: number;
    startHoming: number;
    startCannon: number;
    maxBombs: number;
    maxHoming: number;
    maxCannon: number;
}

interface ShipStats {
    power: number;
    mobility: number;
    shield: number;
    stability: number;
}

interface MissileBarrageConfig {
    count: readonly [number, number, number, number, number];           // ミサイル数 (Lv1-Lv5)
    spreadDuration: readonly [number, number, number, number, number];  // 拡散フェーズ(frames)
    curveDuration: readonly [number, number, number, number, number];   // 曲がりフェーズ(frames)
    homingDuration: number;                                              // 追尾フェーズ(frames)
    spreadAngleRange: number;                                            // 拡散角度範囲 (radians)
    initialSpeed: number;
    homingSpeed: number;
    turnRate: number;
    trailLength: number;
    launchDelay: number;                                                 // 連続発射間隔
}

interface BomberBombConfig {
    damageDuration: readonly [number, number, number, number, number];
    damageInterval: readonly [number, number, number, number, number];
    damage: readonly [number, number, number, number, number];
    effectScale: readonly [number, number, number, number, number];
    missileBarrage: MissileBarrageConfig;
}

interface MissilePersonalityConfig {
    phaseDurationMult: number;  // フェーズ時間倍率
    predictFrames: number;      // 予測フレーム数（0=予測なし）
}

interface MissilePersonalitySettings {
    distribution: readonly [number, number, number];  // バカ, 真面目, 天才の出現率
    baka: MissilePersonalityConfig;
    majime: MissilePersonalityConfig;
    tensai: MissilePersonalityConfig;
}

interface HunterHomingConfig {
    bulletSpeed: readonly [number, number, number, number];
    turnRate: readonly [number, number, number, number];
    trailLength: readonly [number, number, number, number];
    glowSize: readonly [number, number, number, number];
    // 3-phase settings
    spreadDuration: readonly [number, number, number, number];
    spreadAngleRange: number;
    curveDuration: readonly [number, number, number, number];
    curveSpeed: number;
    curveTurnRate: number;
    homingTurnRateMultiplier: number;
    launchDelayMin: number;  // 最小発射間隔（フレーム）
    launchDelayMax: number;  // 最大発射間隔（フレーム）
    // 加速設定
    initialSpeedMult: number;      // 初速倍率（最高速に対する割合）
    accelerationDuration: number;  // 加速フレーム数
    // ミサイル性格設定
    personality: MissilePersonalitySettings;
}

interface LancerCannonConfig {
    duration: readonly [number, number, number, number];
    beamWidth: readonly [number, number, number, number];
    damage: readonly [number, number, number, number];
    colorIntensity: readonly [number, number, number, number];
}

export interface ShipConfig {
    name: string;
    description: string;
    color: readonly [number, number, number];
    ability: 'bomb' | 'homing' | 'cannon';
    moveSpeed: number;
    powerMaxLevel: number;
    powerRequired: readonly number[];
    shieldDuration: number;
    stats: ShipStats;
}

export interface BomberConfig extends ShipConfig {
    ability: 'bomb';
    baseBulletCount: number;     // 基本弾数（パワーレベルに加算）
    bulletDamage: number;        // 弾のダメージ
    bombByPower: BomberBombConfig;
}

export interface HunterConfig extends ShipConfig {
    ability: 'homing';
    baseBulletCount: number;     // 基本弾数（パワーレベルに加算）
    bulletDamage: number;        // 弾のダメージ
    homingDuration: number;
    homingBulletSpeed: number;
    homingTurnRate: number;
    homingAccel: number;
    homingPowerBonus: number;
    homingByPower: HunterHomingConfig;
}

export interface LancerConfig extends ShipConfig {
    ability: 'cannon';
    laserDamage: number;
    laserHitCooldownByPower: readonly [number, number, number, number];
    cannonChargeDuration: number;
    cannonDuration: number;
    cannonDamage: number;
    cannonDamageInterval: number;
    cannonByPower: LancerCannonConfig;
}

interface SpawnConfig {
    baseInterval: number;
    minInterval: number;
    waveReduction: number;
    initialDelay: number;    // ステージ開始後の敵出現遅延（フレーム）
}

interface ItemConfig {
    scoreBonus: number;
}

interface WaveConfig {
    baseEnemiesPerWave: number;
    enemiesIncreasePerWave: number;
    maxEnemiesPerWave: number;
}

interface EnemyProgressionConfig {
    hpBonus: number;
    speedBonus: number;
    sizeBonus: number;
    typeProgressionMax: number;
}

interface StageCommonConfig {
    bgStarsCount: number;
    bgBrightStarsCount: number;
    bgAsteroidsCount: number;
    bgNebulaCount: number;
    flowingStarsCount: number;
    laneMarkerColor: readonly [number, number, number, number];
    flowingLineColor: readonly [number, number, number, number];
}

interface SubspaceColors {
    p1: readonly [number, number, number];
    p2: readonly [number, number, number];
    p3: readonly [number, number, number];
}

interface BackgroundConfig {
    name: string;
    skyTop: readonly [number, number, number];
    skyBottom: readonly [number, number, number];
    starDensity: number;
    hasPlanet?: boolean;
    planetColor?: readonly [number, number, number];
    planetGlow?: readonly [number, number, number];
    hasBrightStars?: boolean;
    hasAsteroids?: boolean;
    hasDistantGiant?: boolean;
    hasNebula?: boolean;
    nebulaColor?: readonly [number, number, number];
    specialEffect?: 'lowOrbit' | 'starfield' | 'asteroidBelt' | 'deepSpace' | 'subspace';
    subspaceColors?: SubspaceColors;
}

interface StagesConfig {
    transitions: readonly [number, number, number, number, number];
    transitionDuration: number;
    nameDisplayDuration: number;
    common: StageCommonConfig;
    backgrounds: readonly BackgroundConfig[];
}

interface StageStartConfig {
    duration: number;
    playerStartY: number;
    playerMidY: number;
    playerEndY: number;
}

interface UIConfig {
    abilityButton: {
        bottomOffset: number;
        rightOffset: number;
        size: number;
        height: number;
    };
}

interface InputConfig {
    keys: {
        left: string[];
        right: string[];
        shoot: string[];
        special: string[];
    };
}

interface WaveStartConfig {
    duration: number;
    textSize: number;
}

interface LayoutConfig {
    canvasWidth: number;   // 全体キャンバス幅 (HUD込み)
    panelWidth: number;    // サイドパネル幅
    gameAreaX: number;     // ゲームエリア開始X座標
}

export interface RankConfig {
    name: string;
    minScore: number;
    minWave: number;
    color: readonly [number, number, number];
    tier: number;
}

interface GameConfig {
    width: number;
    height: number;
    laneCount: number;
    playerSize: number;
    bulletSpeed: number;
    enemyBaseSpeed: number;
    itemSpeed: number;
    itemSpawnChance: number;
    difficulty: {
        easy: DifficultySettings;
        normal: DifficultySettings;
        hard: DifficultySettings;
    };
    ships: {
        bomber: BomberConfig;
        hunter: HunterConfig;
        lancer: LancerConfig;
    };
    spawn: SpawnConfig;
    item: ItemConfig;
    wave: WaveConfig;
    enemy: {
        progression: EnemyProgressionConfig;
    };
    stages: StagesConfig;
    stageStart: StageStartConfig;
    ui: UIConfig;
    input: InputConfig;
    waveStart: WaveStartConfig;
    ranks: readonly RankConfig[];
    layout: LayoutConfig;
}

export const CONFIG: GameConfig = {
    width: 400,              // キャンバスの幅
    height: 700,             // キャンバスの高さ
    laneCount: 2,            // レーンの数（左右2つ）
    playerSize: 40,          // プレイヤーのサイズ
    bulletSpeed: 14,         // 弾の速度
    enemyBaseSpeed: 2.2,     // 敵の基本速度
    itemSpeed: 3.5,          // アイテムの落下速度
    itemSpawnChance: 0.18,   // アイテムのドロップ確率（18%）

    // --- 難易度設定 ---
    difficulty: {
        easy: {
            name: 'EASY',
            color: [100, 255, 100],
            enemySpeedMult: 0.7,      // 敵速度倍率
            enemyHpMult: 0.8,         // 敵HP倍率
            spawnIntervalMult: 1.3,   // 出現間隔倍率（大きいほど遅い）
            itemDropMult: 1.4,        // アイテムドロップ倍率
            bossHpMult: 0.7,          // ボスHP倍率
            startBombs: 5,            // 初期ボム数
            startHoming: 5,           // 初期ホーミング数
            startCannon: 5,           // 初期荷電粒子砲数
            maxBombs: 5,              // 最大ボム数
            maxHoming: 5,             // 最大ホーミング数
            maxCannon: 5              // 最大荷電粒子砲数
        },
        normal: {
            name: 'NORMAL',
            color: [255, 200, 50],
            enemySpeedMult: 1.0,
            enemyHpMult: 1.0,
            spawnIntervalMult: 1.0,
            itemDropMult: 1.0,
            bossHpMult: 1.0,
            startBombs: 3,
            startHoming: 3,
            startCannon: 3,
            maxBombs: 3,
            maxHoming: 3,
            maxCannon: 3
        },
        hard: {
            name: 'HARD',
            color: [255, 80, 80],
            enemySpeedMult: 1.3,
            enemyHpMult: 1.3,
            spawnIntervalMult: 0.7,
            itemDropMult: 0.7,
            bossHpMult: 1.5,
            startBombs: 2,
            startHoming: 2,
            startCannon: 2,
            maxBombs: 2,
            maxHoming: 2,
            maxCannon: 2
        }
    },

    // --- 自機タイプ設定 ---
    ships: {
        bomber: {
            name: 'BOMBER',
            description: '扱いやすい標準機',
            color: [0, 255, 255],
            ability: 'bomb',
            moveSpeed: 1.0,              // 移動速度倍率（標準）
            powerMaxLevel: 5,            // パワー最大レベル
            powerRequired: [1, 2, 3, 5], // 各レベルの必要数
            shieldDuration: 720,         // シールド有効時間（12秒）
            stats: {
                power: 3,       // 火力 (B)
                mobility: 3,    // 機動性 (B)
                shield: 4,      // シールド (A)
                stability: 4    // 安定性 (A)
            },
            // 弾数 = baseBulletCount + powerLevel（Lv1=2発, Lv2=3発, ... Lv5=6発）
            baseBulletCount: 1,          // 基本弾数（パワーレベルに加算）
            bulletDamage: 80,            // 弾ダメージ（調整: 旧100→80）
            // ボムのパワーレベル別設定（Lv1～Lv5）
            // Lv3が基準値、ダメージは10倍
            bombByPower: {
                damageDuration: [60, 75, 90, 105, 120],  // ダメージ持続フレーム
                damageInterval: [24, 22, 20, 18, 16],    // ダメージ間隔
                damage: [300, 300, 300, 300, 300],       // 1回あたりのダメージ
                effectScale: [0.8, 0.9, 1.0, 1.1, 1.2],  // エフェクトの大きさ倍率（控えめ）
                // ミサイル弾幕設定（イタノサーカス風）
                missileBarrage: {
                    count: [12, 16, 20, 26, 32],           // Lv1~Lv5 ミサイル数
                    spreadDuration: [12, 12, 14, 16, 18],  // 拡散フェーズ(frames)
                    curveDuration: [10, 10, 12, 14, 16],   // 曲がりフェーズ(frames)
                    homingDuration: 40,                    // 追尾フェーズ(frames)
                    spreadAngleRange: 1.2,                 // 拡散角度 (±約70度)
                    initialSpeed: 8,
                    homingSpeed: 14,
                    turnRate: 0.15,
                    trailLength: 8,
                    launchDelay: 2                         // 連続発射間隔
                }
            }
        },
        hunter: {
            name: 'HUNTER',
            description: '高機動・低火力',
            color: [255, 150, 0],
            ability: 'homing',
            moveSpeed: 1.8,              // 移動速度倍率（80%速い）
            powerMaxLevel: 4,            // パワー最大レベル（4段階）
            powerRequired: [1, 3, 4],    // 各レベルの必要数
            shieldDuration: 600,         // シールド有効時間（10秒・標準）
            stats: {
                power: 2,       // 火力 (C)
                mobility: 5,    // 機動性 (S)
                shield: 3,      // シールド (B)
                stability: 3    // 安定性 (B)
            },
            // 弾数 = baseBulletCount + powerLevel（Lv1=2発, Lv2=3発, Lv3=4発, Lv4=5発）
            baseBulletCount: 1,          // 基本弾数（パワーレベルに加算）
            bulletDamage: 80,            // 弾ダメージ（調整: 旧100→80）
            homingDuration: 600,         // ホーミング持続時間（10秒）
            homingBulletSpeed: 18,       // ホーミング弾速度（基準）
            homingTurnRate: 0.35,        // ホーミング追尾率
            homingAccel: 0.5,            // 接近時の加速率
            homingPowerBonus: 1,         // ホーミング時パワーレベル+1
            // ホーミングのパワーレベル別設定（Lv1～Lv4）
            // Lv3が現在の基準値、性能向上は緩やか
            homingByPower: {
                bulletSpeed: [15, 16, 18, 20],     // 弾速（緩やかに上昇）
                turnRate: [0.30, 0.32, 0.35, 0.38], // 追尾率（緩やかに上昇）
                trailLength: [16, 18, 20, 22],     // 軌跡の長さ（長めに）
                glowSize: [0.9, 0.95, 1.0, 1.05],  // グローの大きさ倍率（控えめ）
                // 3フェーズ設定
                spreadDuration: [13, 13, 15, 17],   // spread持続時間(frames) +5
                spreadAngleRange: 0.7,              // 拡散角度 ±約40度
                curveDuration: [10, 12, 14, 16],   // curve持続時間(frames)
                curveSpeed: 12,                     // curve時の速度
                curveTurnRate: 0.12,                // curve時のターン率
                homingTurnRateMultiplier: 1.3,      // homing時のターン率倍率
                launchDelayMin: 2,                  // 最小発射間隔（フレーム）
                launchDelayMax: 5,                  // 最大発射間隔（フレーム）
                // 加速設定
                initialSpeedMult: 0.3,              // 初速は最高速の30%
                accelerationDuration: 20,           // 20フレームで最高速に達する
                // ミサイル性格設定
                personality: {
                    distribution: [0.3, 0.4, 0.3],  // バカ, 真面目, 天才の出現率
                    baka: {
                        phaseDurationMult: 1.5,    // フェーズ時間1.5倍（遅い）
                        predictFrames: 0           // 予測なし
                    },
                    majime: {
                        phaseDurationMult: 1.0,    // 標準
                        predictFrames: 0           // 予測なし
                    },
                    tensai: {
                        phaseDurationMult: 0.8,    // フェーズ時間0.8倍（速い）
                        predictFrames: 8           // 8フレーム先を予測（15から調整）
                    }
                }
            }
        },
        lancer: {
            name: 'LANCER',
            description: '高火力・上級者向け',
            color: [200, 50, 255],        // 紫系
            ability: 'cannon',
            moveSpeed: 0.7,              // 移動速度倍率（30%遅い）
            powerMaxLevel: 4,            // パワー最大レベル（4段階）
            powerRequired: [2, 3, 5],    // 各レベルの必要数（上がりにくい）
            shieldDuration: 420,         // シールド有効時間（7秒・短い）
            stats: {
                power: 5,       // 火力 (S)
                mobility: 2,    // 機動性 (C)
                shield: 2,      // シールド (C)
                stability: 1    // 安定性 (D)
            },
            laserDamage: 200,            // レーザー基本ダメージ
            // レーザーの同一敵へのヒット間隔（フレーム）をパワーレベルごとに設定
            laserHitCooldownByPower: [18, 14, 10, 6],
            cannonChargeDuration: 40,    // 荷電粒子砲チャージ時間（2/3秒）
            cannonDuration: 180,         // 荷電粒子砲持続時間（基準・3秒）
            cannonDamage: 500,           // 荷電粒子砲ダメージ
            cannonDamageInterval: 10,    // 荷電粒子砲ダメージ間隔
            // 荷電粒子砲のパワーレベル別設定（Lv1～Lv4）
            // Lv3が現在の基準値
            cannonByPower: {
                duration: [120, 150, 180, 220],    // 持続時間
                beamWidth: [35, 48, 60, 75],       // ビーム幅（基準）
                damage: [300, 400, 500, 700],      // ダメージ
                colorIntensity: [0.6, 0.8, 1.0, 1.2] // 色の強さ倍率
            }
        }
    },

    // --- 敵出現頻度設定 ---
    spawn: {
        baseInterval: 50,        // 基本出現間隔
        minInterval: 18,         // 最小出現間隔
        waveReduction: 3,        // ウェーブごとに減少する間隔
        initialDelay: 90         // ステージ開始後の敵出現遅延（フレーム）約1.5秒
    },


    // --- アイテム設定 ---
    item: {
        scoreBonus: 500          // スコアアイテムのボーナス点
    },

    // --- ウェーブ設定 ---
    wave: {
        baseEnemiesPerWave: 8,   // 基本撃破数（ウェーブ1）
        enemiesIncreasePerWave: 2, // ウェーブごとの撃破数増加
        maxEnemiesPerWave: 25    // 最大撃破数
    },

    // --- 敵の成長設定 ---
    enemy: {
        progression: {
            hpBonus: 0.08,              // ウェーブごとの敵HP増加率（8%）
            speedBonus: 0.04,           // ウェーブごとの敵速度増加率（4%）
            sizeBonus: 0.015,           // ウェーブごとの敵サイズ増加率（1.5%）
            typeProgressionMax: 0.25    // ウェーブによる強敵出現率の最大ボーナス（25%）
        }
    },

    // --- ステージ背景設定 ---
    stages: {
        // 各ステージの開始ウェーブ（このウェーブから背景が変わる）
        transitions: [1, 4, 7, 10, 13], // Wave 1, 4, 7, 10, 13で変化
        transitionDuration: 120, // 遷移演出のフレーム数（2秒）
        nameDisplayDuration: 180, // ステージ名表示時間（3秒）

        // 背景要素の共通設定
        common: {
            bgStarsCount: 120,
            bgBrightStarsCount: 5,
            bgAsteroidsCount: 14,
            bgNebulaCount: 6,
            flowingStarsCount: 80,
            laneMarkerColor: [0, 255, 255, 50],
            flowingLineColor: [0, 255, 255, 20]
        },

        backgrounds: [
            {
                name: 'Low Orbit',
                // 地球低軌道: 青みがかった雰囲気、地球が右下に見える
                skyTop: [8, 15, 35],
                skyBottom: [15, 25, 50],
                starDensity: 0.12,
                hasPlanet: true,
                planetColor: [30, 70, 140],
                planetGlow: [50, 100, 180],
                specialEffect: 'lowOrbit'
            },
            {
                name: 'Starfield',
                // 星空: 深い宇宙、多くの瞬く星、明るい星あり
                skyTop: [2, 3, 12],
                skyBottom: [5, 8, 22],
                starDensity: 1.0,
                hasBrightStars: true,
                specialEffect: 'starfield'
            },
            {
                name: 'Asteroid Belt',
                // 小惑星帯: 茶色がかった宇宙、遠景に巨星
                skyTop: [10, 8, 10],
                skyBottom: [18, 14, 18],
                starDensity: 0.35,
                hasAsteroids: true,
                hasDistantGiant: true, // 遠景の巨大小惑星
                specialEffect: 'asteroidBelt'
            },
            {
                name: 'Deep Space',
                // 深宇宙: 遠景の星雲がゆっくり流れてくる
                skyTop: [1, 2, 3],
                skyBottom: [3, 3, 10],
                starDensity: 0.08,
                hasNebula: true,
                nebulaColor: [70, 35, 100],
                specialEffect: 'deepSpace'
            },
            {
                name: 'Unknown Sector',
                // 未知領域: 紫と赤を含む歪んだ亜空間
                skyTop: [8, 5, 15],
                skyBottom: [15, 8, 25],
                starDensity: 0.0,
                specialEffect: 'subspace',
                subspaceColors: {
                    p1: [80, 22, 110], // 紫
                    p2: [30, 95, 55],  // 緑
                    p3: [115, 22, 35]  // 赤
                }
            }
        ]
    },

    // --- ステージ開始演出設定 ---
    stageStart: {
        duration: 180,           // 自機登場演出時間（フレーム）
        playerStartY: 800,       // 自機の開始Y座標（画面外下）
        playerMidY: 350,         // 中央まで進むY座標
        playerEndY: 620          // 自機の最終Y座標
    },

    // --- UI設定 ---
    ui: {
        abilityButton: {
            bottomOffset: 70,
            rightOffset: 45,
            size: 80,
            height: 100
        }
    },

    // --- 入力設定 ---
    input: {
        keys: {
            left: ['a', 'A', 'ArrowLeft'],
            right: ['d', 'D', 'ArrowRight'],
            shoot: [' '],
            special: ['s', 'S']
        }
    },

    // --- ウェーブ開始演出設定 ---
    waveStart: {
        duration: 75,            // 演出時間（フレーム）- HUDスタイル演出用
        textSize: 36             // テキストサイズ
    },

    // --- ランク設定 ---
    ranks: [
        { name: 'SSS', minScore: 100000, minWave: 15, color: [255, 215, 0], tier: 6 },
        { name: 'SS',  minScore: 50000,  minWave: 10, color: [255, 200, 50], tier: 5 },
        { name: 'S',   minScore: 30000,  minWave: 8,  color: [255, 150, 100], tier: 4 },
        { name: 'A',   minScore: 15000,  minWave: 5,  color: [100, 255, 100], tier: 3 },
        { name: 'B',   minScore: 8000,   minWave: 3,  color: [100, 200, 255], tier: 2 },
        { name: 'C',   minScore: 3000,   minWave: 2,  color: [200, 200, 200], tier: 1 },
        { name: 'D',   minScore: 0,      minWave: 1,  color: [150, 150, 150], tier: 0 }
    ],

    // --- レイアウト設定（HUDパネル付き） ---
    layout: {
        canvasWidth: 640,    // 全体キャンバス幅 (120 + 400 + 120)
        panelWidth: 120,     // 左右パネル幅
        gameAreaX: 120       // ゲームエリア開始X座標
    }
} as const;
